import React, { useMemo, useState } from 'react'
import { withRouter } from 'react-router-dom'
import api from '../../api'
import BaseCheckbox from '../BaseCheckbox'
import BaseModal from '../BaseModal'
import ButtonPrimary from '../ui/ButtonPrimary'
import assignEvenlyBeans from "./assignEvenlyBeans"
import './index.css'

const VarianceListModal = (props) => {
  /** This block determines the counting mode and initializes the loading state for the assignment process. **/
  const isAssignEvenly = props.varianceData?.data?.countingMode === 'part_centric'
  const [isAssignLoading, setIsAssignLoading] = useState(false)

  /** This state hook initializes the team member list with stable UUIDs and assignment counts from props. **/
  const [teamMembers, setTeamMembers] = useState(
    props.usersData?.data?.map(user => ({
      name: `${user.firstName} ${user.lastName}`,
      assignedCount: user.assignedCount || 0,
      UUID: user.UUID,
      isChecked: false,
    })) || []
  )

/** * This state hook initializes the bin list using a lazy initializer function.
   * It only runs ONCE when the modal opens, preventing infinite network loops.
   **/
  const [binList, setBinList] = useState(() => {
    // 1. Safely access the bins array from the nested API structure
    const rawBins = props.varianceData?.data?.bins || props.varianceData?.bins || [];
    
    // 2. Map through bins and filter for only unresolved (active/unassigned) parts
    return rawBins
      .map(bin => {
        const activePartnums = bin.partnums
          ?.filter(p => p.status === 'active' || p.status === 'unassigned')
          .map(p => p.partnum) || [];

        return {
          location: bin.bin,
          partnums: activePartnums,
          isChecked: false,
        };
      })
      // 3. Remove bins that have 0 parts remaining to be counted
      .filter(bin => bin.partnums.length > 0);
  });

  const [memberSearch, setMemberSearch] = useState('')
  const [binSearch, setBinSearch] = useState('')

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase()
    if (!q) return teamMembers
    return teamMembers.filter(m => m.name.toLowerCase().includes(q))
  }, [teamMembers, memberSearch])

  const filteredBins = useMemo(() => {
    const q = binSearch.trim().toLowerCase()
    if (!q) return binList
    return binList.filter(b => b.location.toLowerCase().includes(q))
  }, [binList, binSearch])

  const selectedMemberCount = teamMembers.filter(m => m.isChecked).length
  const selectedBinCount = binList.filter(b => b.isChecked).length

  /** This function updates the selection state of team members using their unique UUID to ensure stable matching. **/
  const checkMember = (uuid) => {
    setTeamMembers(prev => prev.map(m =>
      m.UUID === uuid ? { ...m, isChecked: !m.isChecked } : m
    ))
  }

  /** This function toggles the selection status of specific bins to prepare them for user assignment. **/
  const checkBin = (location) => {
    setBinList(prev => prev.map(b =>
      b.location === location ? { ...b, isChecked: !b.isChecked } : b
    ))
  }

  const setMembersChecked = (uuids, checked) => {
    const set = new Set(uuids)
    setTeamMembers(prev => prev.map(m =>
      set.has(m.UUID) ? { ...m, isChecked: checked } : m
    ))
  }

  const setBinsChecked = (locations, checked) => {
    const set = new Set(locations)
    setBinList(prev => prev.map(b =>
      set.has(b.location) ? { ...b, isChecked: checked } : b
    ))
  }

  const selectAllVisibleMembers = () => setMembersChecked(filteredMembers.map(m => m.UUID), true)
  const clearVisibleMembers = () => setMembersChecked(filteredMembers.map(m => m.UUID), false)
  const selectAllVisibleBins = () => setBinsChecked(filteredBins.map(b => b.location), true)
  const clearVisibleBins = () => setBinsChecked(filteredBins.map(b => b.location), false)

  /** This handler processes the distribution logic for both counting modes and submits the finalized assignments to the API. **/
const sendVarianceList = async () => {
    const selectedMembers = teamMembers.filter(m => m.isChecked)
    if (selectedMembers.length === 0) return

    setIsAssignLoading(true)
    let body = []

    // FIX: Ensure we use the specific ID for the current run session
    const currentRunId = props.varianceData?.data?.id || props.varianceData?.id;

    try {
      if (isAssignEvenly) {
        const flatParts = binList.flatMap(b => b.partnums)
        let currentMemberIdx = 0
        const memberAssignments = selectedMembers.map(m => ({ userUUID: m.UUID, partnums: [] }))

        flatParts.forEach(part => {
          memberAssignments[currentMemberIdx].partnums.push(part)
          currentMemberIdx = (currentMemberIdx + 1) % selectedMembers.length
        })
        body = memberAssignments.filter(m => m.partnums.length > 0)
        
        // Use currentRunId here
        await api.assignPartnumsPartCentric(currentRunId, body)
      } else {
        const selectedBins = binList.filter(b => b.isChecked)
        const assignments = assignEvenlyBeans(selectedMembers, selectedBins)
        Object.keys(assignments).forEach(name => {
          const member = selectedMembers.find(m => m.name === name)
          const binPartnums = []
          assignments[name].bins.forEach(loc => {
            const bin = selectedBins.find(b => b.location === loc)
            bin.partnums.forEach(p => binPartnums.push({ bin: loc, partNumber: p }))
          })
          body.push({ userUUID: member.UUID, binPartnums })
        })

        // Use currentRunId here
        await Promise.all(body.map(v => api.assignPartnumsBinCentric(currentRunId, [v])))
      }
      props.onClose(true)
    } catch (e) {
      console.error("Assignment failed", e)
    } finally {
      setIsAssignLoading(false)
    }
  }

  /** This JSX block defines the modal's internal layout, including team member selection and bin-specific assignment lists. **/
  const membersPane = (
    <div className="variance-list-modal__pane">
      <div className="variance-list-modal__pane-header">
        <span className="variance-list-modal__bold">Step 1.</span>
        <span> Choose Team Members</span>
        <span className="variance-list-modal__count">{selectedMemberCount} of {teamMembers.length} selected</span>
      </div>
      <div className="variance-list-modal__pane-controls">
        <input
          className="variance-list-modal__search"
          type="text"
          placeholder="Search members"
          value={memberSearch}
          onChange={(e) => setMemberSearch(e.target.value)}
        />
        <button type="button" className="variance-list-modal__link-btn" onClick={selectAllVisibleMembers} disabled={filteredMembers.length === 0}>Select all visible</button>
        <button type="button" className="variance-list-modal__link-btn" onClick={clearVisibleMembers} disabled={filteredMembers.length === 0}>Clear</button>
      </div>
      <div className="variance-list-modal__scroll">
        {filteredMembers.length === 0
          ? <p className="variance-list-modal__empty">No members match.</p>
          : (
            <ul className="variance-list-modal__team-list">
              {filteredMembers.map((member) => (
                <li className="variance-list-modal__team-member" key={member.UUID}>
                  <BaseCheckbox
                    checkboxId={member.UUID}
                    isChecked={member.isChecked}
                    checkboxBody={<span>{`${member.name} ${member.assignedCount ? `(${member.assignedCount})` : ''}`}</span>}
                    onCheck={() => checkMember(member.UUID)}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </div>
    </div>
  )

  const binsPane = (
    <div className="variance-list-modal__pane">
      <div className="variance-list-modal__pane-header">
        <span className="variance-list-modal__bold">Step 2.</span>
        <span> Assign Employee To Specific Bin</span>
        <span className="variance-list-modal__count">{selectedBinCount} of {binList.length} selected</span>
      </div>
      <div className="variance-list-modal__pane-controls">
        <input
          className="variance-list-modal__search"
          type="text"
          placeholder="Search bin location"
          value={binSearch}
          onChange={(e) => setBinSearch(e.target.value)}
        />
        <button type="button" className="variance-list-modal__link-btn" onClick={selectAllVisibleBins} disabled={filteredBins.length === 0}>Select all visible</button>
        <button type="button" className="variance-list-modal__link-btn" onClick={clearVisibleBins} disabled={filteredBins.length === 0}>Clear</button>
      </div>
      <div className="variance-list-modal__table-head">
        <span>Location</span>
        <span>&nbsp;Parts #</span>
      </div>
      <div className="variance-list-modal__scroll">
        {filteredBins.length === 0
          ? <p className="variance-list-modal__empty">No bins match.</p>
          : (
            <ul className="variance-list-modal__bin-list">
              {filteredBins.map((bin) => (
                <BaseCheckbox
                  key={bin.location}
                  checkboxClassName="variance-list-modal__bin-item"
                  bodyClassName="variance-list-modal__bin-item-body"
                  checkboxId={bin.location}
                  isChecked={bin.isChecked}
                  checkboxBody={(
                    <>
                      <span className="variance-list-modal__bin-location">{bin.location}</span>
                      <span className="variance-list-modal__bin-parts">{bin.partnums.length}</span>
                    </>
                  )}
                  onCheck={() => checkBin(bin.location)}
                />
              ))}
            </ul>
          )
        }
      </div>
    </div>
  )

  const body = (
    <div className="info-modal variance-list-modal variance-list-modal--scaled">
      <h2 className="info-modal__title">{isAssignEvenly ? "Part Centric Variance" : "Bin Centric Variance"}</h2>

      {isAssignEvenly ? (
        <div className="variance-list-modal__panes variance-list-modal__panes--single">
          {membersPane}
          <div className="variance-list-modal__pane variance-list-modal__pane--note">
            <div className="variance-list-modal__pane-header">
              <span className="variance-list-modal__bold">Step 2.</span>
              <span> Assign Employee By Splitting Part # Evenly</span>
            </div>
            <p>Part # will be evenly assigned to each selected team member.</p>
          </div>
        </div>
      ) : (
        <div className="variance-list-modal__panes">
          {membersPane}
          {binsPane}
        </div>
      )}

      {isAssignLoading && <div className="variance-list-modal__loader"></div>}
    </div>
  )

  /** This block returns the standard modal structure with a disabled state for the submit button until selections are made. **/
  return (
    <BaseModal
      onClose={() => props.onClose(false)}
      body={body}
      footer={
        <div className="variance-list-modal__form-group variance-list-modal__controls variance-list-modal__footer">
          <span className="variance-list-modal__footer-summary">
            {selectedMemberCount} member{selectedMemberCount === 1 ? '' : 's'}
            {!isAssignEvenly && <> · {selectedBinCount} bin{selectedBinCount === 1 ? '' : 's'}</>}
          </span>
          <ButtonPrimary
            isDisabled={isAssignLoading || selectedMemberCount === 0}
            name="Send Variance List"
            onClick={sendVarianceList}
          />
        </div>
      }
    />
  )
}

export default withRouter(VarianceListModal)